import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Typography,
} from '@mui/material'
import RateReviewIcon  from '@mui/icons-material/RateReviewOutlined'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'

interface FinalistView {
  rec_id:    string
  title:     string
  thoughts:  string
}

interface SubmissionRow {
  project_id:                   string
  student_id:                   string
  student_name:                 string
  student_email:                string
  finalists:                    FinalistView[]
  meeting_count_indicator:      string
  updated_at:                   string
}

export default function TopicSubmissionsQueue() {
  const { session, role } = useAuth()
  const navigate          = useNavigate()
  const [rows,    setRows]    = useState<SubmissionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  async function load() {
    if (!session) return
    setLoading(true)
    setError(null)
    try {
      // Pull all submitted research_topic rows
      const { data: topics, error: topicsErr } = await supabase
        .from('research_topic')
        .select('id, project_id, student_id, finalist_recommendation_ids, updated_at')
        .eq('status', 'submitted')
        .order('updated_at', { ascending: true })
      if (topicsErr) throw new Error(topicsErr.message)
      if (!topics?.length) { setRows([]); setLoading(false); return }

      // Scope: mentor/instructor see only their mentees + class students.
      // admin / office_manager see everything.
      let scopedTopics = topics
      if (role === 'mentor' || role === 'instructor') {
        const [{ data: leadAssignments }, { data: classMentors }] = await Promise.all([
          supabase.from('student_mentor').select('student_id')
            .eq('mentor_id', session.user.id).eq('mentor_role', 'lead').eq('status', 'active'),
          supabase.from('class_mentor').select('class_id').eq('user_id', session.user.id),
        ])
        const studentIds = new Set<string>((leadAssignments ?? []).map(r => r.student_id))
        const classIds   = (classMentors ?? []).map(r => r.class_id)
        if (classIds.length > 0) {
          const { data: enrollments } = await supabase
            .from('class_enrollment')
            .select('student_id')
            .in('class_id', classIds)
            .eq('status', 'active')
          for (const e of enrollments ?? []) studentIds.add(e.student_id)
        }
        scopedTopics = topics.filter(t => studentIds.has(t.student_id))
      }

      if (scopedTopics.length === 0) { setRows([]); setLoading(false); return }

      // Resolve student names + finalist titles in parallel
      const studentIds   = scopedTopics.map(t => t.student_id)
      const allFinalists = Array.from(new Set(scopedTopics.flatMap(t => t.finalist_recommendation_ids ?? [])))
      const [{ data: profiles }, { data: recs }] = await Promise.all([
        supabase.from('student_profile').select('student_id, user_id').in('student_id', studentIds),
        allFinalists.length > 0
          ? supabase.from('topic_recommendation').select('id, title, finalist_thoughts').in('id', allFinalists)
          : Promise.resolve({ data: [] as { id: string; title: string; finalist_thoughts: string | null }[] }),
      ])
      const userIds = (profiles ?? []).map(p => p.user_id)
      const { data: users } = userIds.length > 0
        ? await supabase.from('users').select('user_id, first_name, last_name, email').in('user_id', userIds)
        : { data: [] as any[] }
      const userMap: Record<string, any> = Object.fromEntries((users ?? []).map(u => [u.user_id, u]))
      const profileMap: Record<string, string> = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
      const recMap: Record<string, { title: string; thoughts: string }> =
        Object.fromEntries((recs ?? []).map(r => [r.id, { title: r.title, thoughts: r.finalist_thoughts ?? '' }]))

      // Latest "held" step1_review meeting per submission (for the indicator).
      const topicIds = scopedTopics.map(t => t.id)
      const { data: helds } = topicIds.length > 0
        ? await supabase
            .from('meeting')
            .select('research_topic_id, held_at')
            .in('research_topic_id', topicIds)
            .eq('kind', 'step1_review')
            .eq('status', 'held')
            .order('held_at', { ascending: false })
        : { data: [] as { research_topic_id: string; held_at: string }[] }
      const latestHeldByTopic: Record<string, string> = {}
      for (const h of helds ?? []) {
        if (!latestHeldByTopic[h.research_topic_id]) latestHeldByTopic[h.research_topic_id] = h.held_at
      }

      const built: SubmissionRow[] = scopedTopics.map(t => {
        const userId  = profileMap[t.student_id]
        const u       = userId ? userMap[userId] : null
        const ids     = (t.finalist_recommendation_ids ?? []) as string[]
        const heldAt  = latestHeldByTopic[t.id] ?? null
        return {
          project_id:                   t.project_id,
          student_id:                   t.student_id,
          student_name:                 u ? `${u.first_name} ${u.last_name}`.trim() || u.email : '—',
          student_email:                u?.email ?? '',
          finalists:                    ids.map(id => ({
            rec_id:   id,
            title:    recMap[id]?.title    ?? '(unknown)',
            thoughts: recMap[id]?.thoughts ?? '',
          })),
          meeting_count_indicator:      heldAt
            ? `Meeting held ${new Date(heldAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : '',
          updated_at:                   t.updated_at,
        }
      })
      setRows(built)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role])

  // Only show the section when caller can act on it
  const canSee = role === 'admin' || role === 'office_manager' || role === 'mentor' || role === 'instructor'
  if (!canSee) return null

  // Hide the whole panel when there's nothing to action — keeps the page tidy
  if (!loading && rows.length === 0) return null

  return (
    <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Topic Submissions Queue
          </Typography>
          <Chip size="small" label={`${rows.length} awaiting review`} color="primary" />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : (
          rows.map(r => (
            <Box key={r.project_id} sx={{
              py: 2, borderTop: '1px solid', borderColor: 'divider',
              '&:first-of-type': { borderTop: 'none', pt: 0 },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.student_name}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      · {r.student_email}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {r.finalists.length} finalist{r.finalists.length !== 1 ? 's' : ''}: {r.finalists.map(f => f.title).join(' · ')}
                  </Typography>
                </Box>
                <Button
                  size="small" variant="contained"
                  startIcon={<RateReviewIcon fontSize="small" />}
                  onClick={() => navigate(`/review/${r.project_id}`)}
                >
                  Review
                </Button>
              </Box>
              {r.meeting_count_indicator && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {r.meeting_count_indicator}
                </Typography>
              )}
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  )
}
